import {
  SiteEntryProps,
  Site,
  SiteBody,
  WatcherrApiError,
  Plane,
  PlaneEntryProps,
  FloorPlanBody,
} from '@ixicare/resources';
import { action, makeAutoObservable, runInAction } from 'mobx';
import { State } from '~@types/store';
import { UserAction } from '~config/notification';
import { t } from '~i18n';
import { humaniseErrorToMessage } from '~utils/error';

class SiteStore {
  rootStore: State;
  id: number;
  uuid: string;
  name: string;
  address: string;
  city: string;
  postalCode: string;
  country: string;
  lat: number;
  long: number;
  alertContactName: string;
  alertContactPhoneNumber: string;
  alertContactEmail: string;
  doorCode: string;
  vatNumber: string;
  slug: string;
  planes: PlaneEntryProps[];
  isDemokit: boolean;
  organizationId: string | null;
  error?: WatcherrApiError;

  constructor(rootStore: State, building: SiteEntryProps) {
    makeAutoObservable(this, { uploadFloorMap: action.bound });
    this.rootStore = rootStore;
    this.id = building.id;
    this.uuid = building.uuid;
    this.name = building.name;
    this.address = building.address;
    this.city = building.city;
    this.postalCode = building.postalCode;
    this.country = building.country;
    this.lat = building.lat;
    this.long = building.long;
    this.alertContactName = building.alertContactName;
    this.alertContactPhoneNumber = building.alertContactPhoneNumber;
    this.alertContactEmail = building.alertContactEmail;
    this.doorCode = building.doorCode;
    this.vatNumber = building.vatNumber;
    this.slug = building.slug;
    this.planes = building.planes;
    this.isDemokit = building.isDemokit;
    this.organizationId = building.organizationId;
  }

  setProps(profile: SiteEntryProps) {
    Object.keys(profile).forEach((key) => {
      this[key] = profile[key];
    });
  }

  updateBuilding(body: SiteBody): Promise<SiteStore> {
    return new Promise((resolve, reject) => {
      Site.patchSite(this.id, body)
        .then((site) => {
          this.setProps(site);
          this.rootStore.notifications.addUserActionNotification({
            type: UserAction.SiteUpdate,
            notificationType: 'success',
            message: t('alerts.site.update.success', { siteName: body.name }),
          });
          const buildingToUpdate = [...this.rootStore.sites.sites].find((s) => s.id === site.id);

          if (buildingToUpdate) {
            buildingToUpdate.setProps(site);
          }
          resolve(this);
        })
        .catch((res) => {
          runInAction(() => {
            this.error = res;
            this.rootStore.notifications.addUserActionNotification({
              type: UserAction.SiteUpdate,
              notificationType: 'error',
              message: this.error
                ? humaniseErrorToMessage(this.error)
                : t('alerts.site.update.failure', { siteName: body.name }),
            });
          });
          reject();
        });
    });
  }

  updatePlane(floorId: number, body: FloorPlanBody): Promise<PlaneEntryProps> {
    return new Promise((resolve, reject) => {
      Plane.updatePlane(floorId, body)
        .then((data) => {
          this.rootStore.notifications.addUserActionNotification({
            type: UserAction.SiteFloorCreate,
            notificationType: 'success',
            message: t('alerts.site.floor.update.success', { floorName: body.floorName }),
          });
          resolve(data);
        })
        .catch((e: Error) => {
          reject(e);
        });
    });
  }

  async refresh(): Promise<void> {
    const updatedProps = await Site.getSite(this.id);
    runInAction(() => {
      this.setProps(updatedProps);
    });
  }

  uploadFloorMap(props: FloorPlanBody): Promise<PlaneEntryProps> {
    return new Promise((resolve) => {
      Site.submitFloor(this.id, props).then((data) => {
        this.rootStore.notifications.addUserActionNotification({
          type: UserAction.SiteFloorCreate,
          notificationType: 'success',
          message: t('alerts.site.floor.created.success', { floorName: props.floorName }),
        });
        resolve(data);
      });
    });
  }

  archivePlane(planeUuid: string): Promise<PlaneEntryProps> {
    return new Promise((resolve, reject) => {
      Plane.archivePlane(planeUuid)
        .then((data) => {
          runInAction(() => {
            this.rootStore.notifications.addUserActionNotification({
              type: UserAction.SitePlaneArchive,
              notificationType: 'success',
              message: t('alerts.site.floor.archive.success', { floorName: data.floorName }),
            });
            this.planes = this.planes.filter((plane) => plane.uuid !== planeUuid);
          });
          resolve(data);
        })
        .catch((res) => {
          runInAction(() => {
            this.error = res;
            this.rootStore.notifications.addUserActionNotification({
              type: UserAction.SitePlaneArchive,
              notificationType: 'error',
              message: this.error ? humaniseErrorToMessage(this.error) : t('alerts.site.floor.archive.failure'),
            });
          });

          reject();
        });
    });
  }
}

export default SiteStore;
